import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import { regenerateCommonSummary } from '@/hooks/apiHooks';
import { useSnackbar } from '@/hooks/global-tools';
import { CommonSummaryStatusState } from '@/recoil/atom/documents';

// SSE 가 끝내 안 오는 경우를 대비한 안전 타임아웃 (ms) — 스켈레톤이 영원히 남는 것만 막는다
const SAFETY_TIMEOUT = 3 * 60 * 1000;

/**
 * 회의록 공통 요약본(AI 요약) 재생성 훅.
 *
 * 상태는 SSE 이벤트를 그대로 따른다 (useBatchPollingAiSummaries 가 atom 에 기록):
 *   regenerate.start    → loading  (스켈레톤)
 *   regenerate.complete → 항목 삭제 + 문서 재조회 (갱신된 요약 표시)
 *   regenerate.error    → error    (오류 문구)
 *
 * refresh 버튼은 POST 만 호출하고, loading 은 SSE start 를 기다리지 않고 즉시 켠다
 * (클릭 반응이 늦어 보이지 않게). 상태는 전역 atom 에 있어 모달을 닫았다 열어도 유지된다.
 */
const useCommonSummaryRegenerate = (docId: string) => {
  const { showError } = useSnackbar();

  const [statusMap, setStatusMap] = useAtom(CommonSummaryStatusState);
  const entry = statusMap[docId];

  const clearEntry = useCallback(() => {
    setStatusMap((prev) => {
      if (!prev[docId]) return prev;
      const next = { ...prev };
      delete next[docId];
      return next;
    });
  }, [docId, setStatusMap]);

  // SSE 유실 대비 — loading 이 안전 타임아웃을 넘기면 상태를 걷어 스켈레톤을 내린다
  useEffect(() => {
    if (entry?.status !== 'loading') return;
    const remaining = SAFETY_TIMEOUT - (Date.now() - entry.startedAt);
    const timer = setTimeout(clearEntry, Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [entry, clearEntry]);

  const regenerate = useCallback(async () => {
    if (entry?.status === 'loading') return;
    setStatusMap((prev) => ({
      ...prev,
      [docId]: { status: 'loading', startedAt: Date.now() },
    }));
    try {
      await regenerateCommonSummary(docId);
    } catch (e) {
      clearEntry();
      showError({ message: 'AI 요약 생성에 실패했습니다.' });
    }
  }, [docId, entry, setStatusMap, clearEntry, showError]);

  return {
    regenerate,
    isRegenerating: entry?.status === 'loading',
    regenerateFailed: entry?.status === 'error',
  };
};

export default useCommonSummaryRegenerate;
